import { Box } from '@chakra-ui/react';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useReducer, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import useChat from './hooks/useChat';
import useSyncUser from './hooks/useSyncUser';
import Navbar from './components/Navbar';

import About from './pages/About';
import Contact from './pages/Contact';
import Changelog from './pages/Changelog';
import Tutorial from './pages/Tutorial';
import Home from './pages/Home';
import Browse from './pages/Browse';
import SeekingAlphaSuggest from './pages/SeekingAlphaSuggest';
import StockBaskets from './pages/StockBaskets';
import StockBasketCreate from './pages/StockBasketCreate';
import StockBasketDetail from './pages/StockBasketDetail';
import StockPage from './pages/stock/StockPage';
import StockHome from './pages/stock/StockHome';
import StockAnalysis from './pages/stock/StockAnalysis';
import StockFinancialStatements, { LegacyEarningsRedirect } from './pages/stock/StockFinancialStatements';
import StockSecFilings from './pages/stock/StockSecFilings';
import StockReverseDcf from './pages/stock/StockReverseDcf';
import StockCharts from './pages/stock/StockCharts';
import StockSnapshot from './pages/stock/StockSnapshot';
import PublicMetricPage from './pages/public/PublicMetricPage';
import PublicMetricsIndexPage from './pages/public/PublicMetricsIndexPage';
import PublicStockMetricsPage from './pages/public/PublicStockMetricsPage';
import { useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import PostLogout from './pages/PostLogout';
import Ratings from './pages/Ratings';
import Settings from './pages/Settings';
import ApiDocs from './pages/ApiDocs';
import NotFound from './pages/NotFound';
import CustomMetricsAdmin from './pages/CustomMetricsAdmin';
import InsiderProfile from './pages/InsiderProfile';
import PreIpoSecFilings from './pages/PreIpoSecFilings';
import { isAdminUser } from './utils/adminAccess';

const initialTutorialSections = [
  { index: 0, title: 'Step 0: Introduction', content: 'Welcome to DCF Teacher!', unlocked: true, completed: true },
  { index: 1, title: 'Step 1: What are Discounted Cash Flows?', content: 'Welcome to DCF Teacher!', unlocked: true, completed: true },
  { index: 2, title: 'Step 2: Select a Company', content: 'Select a company to analyze.', unlocked: true, completed: false },
  { index: 3, title: 'Step 3: Gather Data', content: 'Gather financial data for the selected company.', unlocked: false, completed: false },
  // { index: 4, title: 'Step 4: Calculate Free Cash Flow', content: 'Calculate the Free Cash Flow.', unlocked: false, completed: false },
  // { index: 5, title: 'Step 5: Determine Growth Rate', content: 'Determine the growth rate of Free Cash Flow.', unlocked: false, completed: false },
  { index: 4, title: 'Step 4: Set Discount and Growth Rate', content: 'Select an appropriate discount rate.', unlocked: false, completed: false },
  // Add more sections as needed
];

const initialTutorialState = {
  currentSection: 0,
  selectedStock: null,
  selectedStockTicker: null,
  sectionCompleted: false,
  sections: initialTutorialSections,
};

function tutorialReducer(state, action) {
  switch (action.type) {
    case 'SET_CURRENT_SECTION':
      return { ...state, currentSection: action.payload };
    case 'SET_SECTION_COMPLETED':
      return { ...state, sectionCompleted: action.payload };
    case 'UPDATE_SECTION_COMPLETED':
      return {
        ...state,
        sections: state.sections.map((section) =>
          section.index === action.payload.sectionIndex
            ? { ...section, completed: action.payload.isCompleted }
            : section
        ),
      };
    case 'SELECT_STOCK':
      return {
        ...state,
        selectedStock: action.payload.stock,
        selectedStockTicker: action.payload.ticker,
      };
    case 'COMPLETE_SECTION': {
      const { currentSection, sections } = state;
      return {
        ...state,
        currentSection: currentSection + 1,
        sections: sections.map((section, index) => {
          if (index === currentSection) {
            return { ...section, completed: true };
          }
          if (index === currentSection + 1) {
            return { ...section, unlocked: true };
          }
          return section;
        }),
      };
    }
    default:
      return state;
  }
}

function AdminOnlyRoute({ children }) {
  const { isAuthenticated, isLoading, user } = useAuth0();
  if (isLoading) {
    return <Box py={8} />;
  }
  if (!isAuthenticated || !isAdminUser(user)) {
    return <NotFound />;
  }
  return children;
}

function App() {
  const [
    { currentSection, selectedStock, selectedStockTicker, sections },
    dispatchTutorial,
  ] = useReducer(tutorialReducer, initialTutorialState);
  const { chatHistory, isLoading, userMessage, setUserMessage, sendMessage } = useChat();

  useSyncUser();

  function updateSectionCompleted(sectionIndex, isCompleted) {
    dispatchTutorial({
      type: 'UPDATE_SECTION_COMPLETED',
      payload: { sectionIndex, isCompleted },
    });
  }

  const completeSection = () => {
    dispatchTutorial({ type: 'COMPLETE_SECTION' });
  };

  const navigateToSection = (index) => {
    dispatchTutorial({ type: 'SET_CURRENT_SECTION', payload: index });
  };

  const setSectionCompleted = (value) => {
    dispatchTutorial({ type: 'SET_SECTION_COMPLETED', payload: value });
  };

  const handleStockSelect = (stockTicker) => {
    const stockDetails = {
      'aapl': { cik: '0000320193', accessionNumber: '0000320193-21-000065', ticker: 'aapl' },
      'tsla': { cik: '0001318605', accessionNumber: '0001318605-21-000063', ticker: 'tsla' },
      'nke': { cik: '0000320187', accessionNumber: '0000320187-21-000056', ticker: 'nke' },
    };

    dispatchTutorial({
      type: 'SELECT_STOCK',
      payload: { stock: stockDetails[stockTicker], ticker: stockTicker },
    });
    completeSection();
  };

  const onSubmitAnswers = (answers) => {
    console.log(answers);
  };

  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const isStockPage = location.pathname.startsWith('/stock/');
  const ticker = isStockPage ? location.pathname.split('/')[2] : null;

  return (
    <>
      
      {isStockPage && (
        <Sidebar ticker={ticker} isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
      )}

      <Box
        as="nav"
        position="fixed"
        top="0"
        left="0"
        w="100%"
        h="var(--nav-h)"
        pl={{ base: "0", md: isStockPage ? "60px" : "0" }}
        transform={{ base: "translateX(0)", md: isStockPage && isExpanded ? "translateX(200px)" : "translateX(0)" }}
        transition="transform 0.3s ease"
        willChange="transform"
        zIndex={998}
        >
        <Navbar />
      </Box>
      <Box
        as="main"
        pt="var(--nav-h)"
        pl={{ base: "0", md: isStockPage ? "60px" : "0" }}
        transform={{ base: "translateX(0)", md: isStockPage && isExpanded ? "translateX(200px)" : "translateX(0)" }}
        transition="transform 0.3s ease"
        willChange="transform"
        overflowX="clip"
      >
        <Routes>
          <Route path="/" element={
            <Home handleStockSelect={handleStockSelect} />
          } />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/changelog" element={<Changelog />} />
          <Route path="/browse" element={
            <Browse handleStockSelect={handleStockSelect} />
          } />
          <Route path="/stock-baskets" element={<StockBaskets />} />
          <Route path="/stock-baskets/new" element={<StockBasketCreate />} />
          <Route path="/stock-baskets/:basketId" element={<StockBasketDetail />} />
          <Route path="/beta/seeking-alpha" element={<SeekingAlphaSuggest />} />
          <Route path="/ratings" element={<Ratings />} />
          <Route path="/insider/:cik" element={<InsiderProfile />} />
          <Route path="/company/:cik/sec-filings" element={<PreIpoSecFilings />} />
          <Route path="/profile" element={<Settings />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/docs" element={<ApiDocs />} />
          <Route path="/api-docs" element={<ApiDocs />} />
          <Route path="/metrics" element={<PublicMetricsIndexPage />} />
          <Route path="/metrics/:categorySlug" element={<PublicMetricsIndexPage />} />
          <Route path="/stocks/:ticker/metrics" element={<PublicStockMetricsPage />} />
          <Route path="/stocks/:ticker/metrics/:metricSlug" element={<PublicMetricPage />} />
          <Route path="/admin/custom-metrics" element={
            <AdminOnlyRoute>
              <CustomMetricsAdmin />
            </AdminOnlyRoute>
          } />
          <Route path="/tutorials" element={
            <Tutorial
              sections={sections}
              currentSection={currentSection}
              navigateToSection={navigateToSection}
              chatHistory={chatHistory}
              isLoading={isLoading}
              userMessage={userMessage}
              setUserMessage={setUserMessage}
              sendMessage={sendMessage}
              selectedStock={selectedStock}
              selectedStockTicker={selectedStockTicker}
              onSubmitAnswers={onSubmitAnswers}
              updateSectionCompleted={updateSectionCompleted}
              setSectionCompleted={setSectionCompleted}
              completeSection={completeSection}
              handleStockSelect={handleStockSelect}
            />
          } />
          <Route path="/stock/:ticker" element={<StockPage />}>
            <Route index element={<StockHome />} />
            <Route path="analysis" element={<StockAnalysis />} />
            <Route path="reverse-dcf" element={<StockReverseDcf />} />
            <Route path="charts" element={<StockCharts />} />
            <Route path="snapshot" element={<StockSnapshot />} />
            <Route path="financials">
              <Route index element={<StockFinancialStatements />} />
              <Route path=":statementSlug" element={<StockFinancialStatements />} />
            </Route>
            <Route path="earnings" element={<LegacyEarningsRedirect />} />
            <Route path="sec-filings" element={<StockSecFilings />} />
          </Route>
          <Route path="/post-logout" element={<PostLogout />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
    </>
  );
}

export default App;
